"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Methodology() {
  const {
    ref: row1Ref,
    isVisible: row1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: row2Ref,
    isVisible: row2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const safetySpecs = [{
    label: "Environment",
    value: "Neutral/Safe"
  }, {
    label: "Approach",
    value: "Trauma-Informed"
  }, {
    label: "Focus",
    value: "Child-Centered"
  }, {
    label: "Reporting",
    value: "Court-Ready"
  }];
  const supportSpecs = [{
    label: "Service Type",
    value: "Supervised"
  }, {
    label: "Availability",
    value: "Arizona-Wide"
  }, {
    label: "Philosophy",
    value: "Firm Boundaries"
  }, {
    label: "Goal",
    value: "Family Stability"
  }];
  return <section data-section-id="2098" id="methodologyoverview" className="relative overflow-hidden bg-[#E7D8C7] py-28 md:py-36">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
        <span className="font-default font-light leading-none" style={{
        fontSize: "clamp(8rem, 16vw, 16rem)",
        color: "transparent",
        WebkitTextStroke: "0.5px rgba(143, 162, 124, 0.2)"
      }}>
          01
        </span>
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        {}
        <div ref={row1Ref} data-index={0} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 transition-all duration-700 ease-[cubic-bezier(0.7,0,0.2,1)] ${row1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="relative lg:col-span-7 -ml-6 md:-ml-12 lg:-ml-12">
            <div className="relative aspect-[7/5] w-full overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safe_environment_support.png" alt="A calm, neutral, and structured space designed for supervised parenting time" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              {}
              <CoordinateTick className="top-3 left-3" />
              <CoordinateTick className="top-3 right-3" />
              <CoordinateTick className="bottom-3 left-3" />
              <CoordinateTick className="bottom-3 right-3" />
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pl-20 flex flex-col justify-center">
            {}
            <div className="font-default uppercase mb-3" style={{
            fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
            fontWeight: 600,
            letterSpacing: "0.18em",
            color: "#5E6A52"
          }}>
              § 01 — OUR APPROACH / I
            </div>

            {}
            <h3 className="font-default text-[#2F3631]" style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
            fontWeight: 600,
            letterSpacing: "-0.015em",
            lineHeight: 1.12
          }}>
              Safety, built on <em className="italic font-normal">neutrality</em>.
            </h3>

            {}
            <div className="mt-6 flex items-center">
              <div className="flex-1" style={{
              height: "0.5px",
              backgroundColor: "rgba(94, 106, 82, 0.4)"
            }} />
              <span className="ml-0 inline-block" style={{
              width: "4px",
              height: "4px",
              backgroundColor: "#A3B18A"
            }} />
            </div>

            {}
            <p className="mt-8 font-default" style={{
            fontSize: "clamp(0.9375rem, 0.95vw, 1rem)",
            fontWeight: 400,
            lineHeight: 1.65,
            color: "#2F3631"
          }}>
              At Tiny to Teen Supervised Parenting & Education Services, we provide a secure, structured environment where families can navigate complex transitions. Our trauma-informed approach ensures that every interaction remains child-centered, neutral, and professional, providing the stability families need during court-involved matters.
            </p>

            {}
            <dl className="mt-10">
              {safetySpecs.map((spec, i) => <div key={spec.label} data-index={i} className="flex items-baseline justify-between py-3" style={{
              borderTop: i === 0 ? "0.5px solid rgba(94, 106, 82, 0.2)" : "none",
              borderBottom: "0.5px solid rgba(94, 106, 82, 0.2)"
            }}>
                  <dt className="font-default uppercase" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.08em",
                color: "#5E6A52"
              }}>
                    {spec.label}
                  </dt>
                  <dd className="font-default uppercase" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.08em",
                color: "#2F3631"
              }}>
                    {spec.value}
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>

        {}
        <div ref={row2Ref} data-index={1} className={`mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 transition-all duration-700 ease-[cubic-bezier(0.7,0,0.2,1)] ${row2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="order-2 lg:order-1 lg:col-span-5 lg:pr-20 flex flex-col justify-center">
            <div className="font-default uppercase mb-3" style={{
            fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
            fontWeight: 600,
            letterSpacing: "0.18em",
            color: "#5E6A52"
          }}>
              § 01 — OUR APPROACH / II
            </div>

            <h3 className="font-default text-[#2F3631]" style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
            fontWeight: 600,
            letterSpacing: "-0.015em",
            lineHeight: 1.12
          }}>
              Compassionate support, <em className="italic font-normal">firmly</em> grounded.
            </h3>

            <div className="mt-6 flex items-center">
              <span className="inline-block mr-0" style={{
              width: "4px",
              height: "4px",
              backgroundColor: "#A3B18A"
            }} />
              <div className="flex-1" style={{
              height: "0.5px",
              backgroundColor: "rgba(94, 106, 82, 0.4)"
            }} />
            </div>

            <p className="mt-8 font-default" style={{
            fontSize: "clamp(0.9375rem, 0.95vw, 1rem)",
            fontWeight: 400,
            lineHeight: 1.65,
            color: "#2F3631"
          }}>
              We believe that firm boundaries are the foundation of compassionate care. By maintaining clear, consistent expectations, we help families focus on what matters most: the well-being of the children involved. Our services are designed to be steady, reliable, and respectful of all parties.
            </p>

            <dl className="mt-10">
              {supportSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="flex items-baseline justify-between py-3" style={{
              borderTop: i === 0 ? "0.5px solid rgba(94, 106, 82, 0.2)" : "none",
              borderBottom: "0.5px solid rgba(94, 106, 82, 0.2)"
            }}>
                  <dt className="font-default uppercase" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.08em",
                color: "#5E6A52"
              }}>
                    {spec.label}
                  </dt>
                  <dd className="font-default uppercase" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.08em",
                color: "#2F3631"
              }}>
                    {spec.value}
                  </dd>
                </div>)}
            </dl>
          </div>

          {}
          <div className="order-1 lg:order-2 relative lg:col-span-7 -mr-6 md:-mr-12 lg:-mr-12">
            <div className="relative aspect-[7/5] w-full overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/family_support_services.png" alt="Professional family support services in a structured Arizona setting" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              <CoordinateTick className="top-3 left-3" />
              <CoordinateTick className="top-3 right-3" />
              <CoordinateTick className="bottom-3 left-3" />
              <CoordinateTick className="bottom-3 right-3" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function CoordinateTick({
  className = ""
}: {
  className?: string;
}) {
  return <span aria-hidden="true" className={`absolute pointer-events-none ${className}`} style={{
    width: "8px",
    height: "8px"
  }}>
      <span className="absolute left-1/2 top-0 -translate-x-1/2" style={{
      width: "0.5px",
      height: "8px",
      backgroundColor: "rgba(94, 106, 82, 0.4)"
    }} />
      <span className="absolute top-1/2 left-0 -translate-y-1/2" style={{
      width: "8px",
      height: "0.5px",
      backgroundColor: "rgba(94, 106, 82, 0.4)"
    }} />
    </span>;
}